/**
 * App-level audio singleton (ADR 0001). Owns the one {@link AudioContext} and the two
 * master buses that everything routes through:
 *
 *   - SFX bus   (gain = `sfxVolume`)   ← hits/thrusts ({@link HitAudio}) + UI clicks
 *   - music bus (gain = `musicVolume`) ← the looping background track
 *
 * It outlives any single VR session because the music and the UI click also play on the
 * 2D landing, before a session exists. The {@link ImpactFeedback} seam is unchanged: its
 * audio sink ({@link HitAudio}) simply pulls the shared context + SFX bus from here, so
 * tests still inject a fake at the seam.
 *
 * Browser autoplay policy keeps the context suspended until a user gesture; the music
 * therefore starts on the first click/keypress on the landing (see {@link armAudioOnFirstGesture}).
 * With no `AudioContext` (unit tests, old runtimes) every entry point degrades to a no-op.
 */
import {
  AUDIO_CLICK_DURATION_S,
  AUDIO_CLICK_FREQ_HZ,
  AUDIO_CLICK_GAIN,
  AUDIO_ERROR_DURATION_S,
  AUDIO_ERROR_FREQ_END_HZ,
  AUDIO_ERROR_FREQ_START_HZ,
  AUDIO_ERROR_GAIN,
  AUDIO_MUSIC_URL,
} from '../config/constants';
import { getSetting, subscribeSettings } from '../settings/userSettings';

type AudioCtxCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let sfxBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let musicStarted = false;
let settingsBound = false;

function getCtor(): AudioCtxCtor | null {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  const w = globalThis as { webkitAudioContext?: AudioCtxCtor };
  return w.webkitAudioContext ?? null;
}

/** Lazily create the shared context + buses. Returns null where WebAudio is unavailable. */
function ensure(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = getCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  sfxBus = ctx.createGain();
  sfxBus.connect(ctx.destination);
  musicBus = ctx.createGain();
  musicBus.connect(ctx.destination);
  applyVolumes();
  if (!settingsBound) {
    subscribeSettings(applyVolumes);
    settingsBound = true;
  }
  return ctx;
}

/** Push the current `sfxVolume` / `musicVolume` settings onto the two buses. */
function applyVolumes(): void {
  if (!ctx || !sfxBus || !musicBus) return;
  sfxBus.gain.setValueAtTime(getSetting('sfxVolume'), ctx.currentTime);
  musicBus.gain.setValueAtTime(getSetting('musicVolume'), ctx.currentTime);
}

/** The shared destination for the hit-sound player. Null where WebAudio is unavailable. */
export function getSfxDestination(): { ctx: AudioContext; bus: GainNode } | null {
  const c = ensure();
  if (!c || !sfxBus) return null;
  return { ctx: c, bus: sfxBus };
}

/** A short synthesised tone on the SFX bus — the universal UI "click". Fire-and-forget. */
export function playUiClick(): void {
  const c = ensure();
  if (!c || !sfxBus) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(AUDIO_CLICK_FREQ_HZ, now);
  const env = c.createGain();
  // Tiny attack + decay envelope so the burst doesn't pop.
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(AUDIO_CLICK_GAIN, now + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, now + AUDIO_CLICK_DURATION_S);
  osc.connect(env).connect(sfxBus);
  osc.start(now);
  osc.stop(now + AUDIO_CLICK_DURATION_S + 0.02);
}

/**
 * A short synthesised "blorp" on the SFX bus — the arcade wrong-direction error cue.
 * A falling pitch sweep on a sawtooth voice reads as "rejected". Fire-and-forget.
 */
export function playArcadeError(): void {
  const c = ensure();
  if (!c || !sfxBus) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(AUDIO_ERROR_FREQ_START_HZ, now);
  osc.frequency.exponentialRampToValueAtTime(AUDIO_ERROR_FREQ_END_HZ, now + AUDIO_ERROR_DURATION_S);
  const env = c.createGain();
  // Quick attack + decay envelope so the burst doesn't pop.
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(AUDIO_ERROR_GAIN, now + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, now + AUDIO_ERROR_DURATION_S);
  osc.connect(env).connect(sfxBus);
  osc.start(now);
  osc.stop(now + AUDIO_ERROR_DURATION_S + 0.02);
}

/** Begin (idempotently) the looping background track on the music bus. Missing file = silent. */
export function startMusic(): void {
  if (musicStarted) return;
  const c = ensure();
  if (!c || !musicBus) return;
  const bus = musicBus;
  musicStarted = true;
  void (async () => {
    try {
      const res = await fetch(AUDIO_MUSIC_URL);
      const buf = await c.decodeAudioData(await res.arrayBuffer());
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(bus);
      src.start();
    } catch (err) {
      // No track shipped (or decode failed): leave the bus silent.
      console.warn('[audio] background music unavailable', err);
      musicStarted = false;
    }
  })();
}

/**
 * Arm the audio on the first user gesture: resume the (autoplay-suspended) context and
 * start the background music. One-shot — the listeners remove themselves once fired.
 */
export function armAudioOnFirstGesture(): void {
  if (typeof window === 'undefined') return;
  const onGesture = (): void => {
    window.removeEventListener('pointerdown', onGesture);
    window.removeEventListener('keydown', onGesture);
    const c = ensure();
    if (c && c.state === 'suspended') void c.resume().catch(() => {});
    startMusic();
  };
  window.addEventListener('pointerdown', onGesture);
  window.addEventListener('keydown', onGesture);
}
